<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    /**
     * The shape of the card.
     */
    shape?: 'straight' | 'rounded' | 'smooth' | 'curved'

    /**
     * Whether the card is elevated.
     */
    elevated?: boolean

    /**
     * Whether the card is elevated on hover.
     */
    elevatedHover?: boolean

    /**
     * The color of the card.
     */
    color?: 'white' | 'white-contrast' | 'muted' | 'muted-contrast' | 'primary' | 'info' | 'success' | 'warning' | 'danger' | 'none'
  }>(),
  {
    shape: undefined,
    elevated: false,
    elevatedHover: false,
    color: 'white',
  },
)

const { defaultShapes } = useThemeShapes()

const shape = computed(() => props.shape ?? defaultShapes.value?.card)

// Tailwind class mappings
const shapeClasses = {
  straight: '',
  rounded: 'rounded-md',
  smooth: 'rounded-lg',
  curved: 'rounded-xl',
}

const colorClasses = {
  'white': 'bg-white dark:bg-muted-800 border border-muted-200 dark:border-muted-700',
  'white-contrast': 'bg-white dark:bg-muted-950 border border-muted-300 dark:border-muted-800',
  'muted': 'bg-muted-50 dark:bg-muted-800 border border-muted-200 dark:border-muted-700',
  'muted-contrast': 'bg-muted-100 dark:bg-muted-950 border border-muted-300 dark:border-muted-800',
  'primary': 'bg-primary-500/10 dark:bg-primary-500/20 border border-primary-500/20 dark:border-primary-500/30',
  'info': 'bg-info-500/10 dark:bg-info-500/20 border border-info-500/20 dark:border-info-500/30',
  'success': 'bg-success-500/10 dark:bg-success-500/20 border border-success-500/20 dark:border-success-500/30',
  'warning': 'bg-warning-500/10 dark:bg-warning-500/20 border border-warning-500/20 dark:border-warning-500/30',
  'danger': 'bg-danger-500/10 dark:bg-danger-500/20 border border-danger-500/20 dark:border-danger-500/30',
  'none': '',
}

const classes = computed(() => [
  // Base classes
  // 'relative', // removed this, because we want some cards to be absolute, and this made it hard to overwrite that.
  // Shape classes
  shape.value && shapeClasses[shape.value as keyof typeof shapeClasses],
  // Color classes
  colorClasses[props.color as keyof typeof colorClasses],
  // Elevation classes
  props.elevated && 'shadow-lg dark:shadow-muted-800/50',
  props.elevatedHover && 'transition-shadow duration-300 hover:shadow-lg dark:hover:shadow-muted-800/50',
])
</script>

<template>
  <div :class="classes">
    <slot />
  </div>
</template>
